/**
 * OFPilot - Organization Branding Context
 * ========================================
 * Provides organization branding settings globally across the app.
 * Falls back to OFPilot default branding if not configured.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { getInMemoryToken } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default OFPilot branding
const DEFAULT_BRANDING = {
  organizationId: null,
  organizationName: 'OFPilot',
  logoUrl: null,
  primaryColor: '#6366F1',
  secondaryColor: '#8B5CF6',
  faviconUrl: null,
  emailSignatureName: 'OFPilot',
  // Platform constants
  platformName: 'OFPilot',
  platformLogoDark: '/images/ofpilot-logo-new.png',
  platformLogoLight: '/images/ofpilot-logo-new.png',
  poweredByText: 'Powered by OFPilot',
  emailFooterText: 'Envoyé via la plateforme OFPilot',
  pdfFooterText: 'OFPilot – plateforme de gestion de formation',
  // Loading state
  isLoaded: false
};

const BrandingContext = createContext(DEFAULT_BRANDING);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    return DEFAULT_BRANDING;
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [cssVariablesApplied, setCssVariablesApplied] = useState(false);

  // Load branding from API.
  // Guards against calling when unauthenticated to prevent the api.js 401
  // interceptor from hard-redirecting to /login while already on that page.
  const loadBranding = useCallback(async () => {
    if (!getInMemoryToken()) {
      setBranding({ ...DEFAULT_BRANDING, isLoaded: true });
      return;
    }
    try {
      const response = await api.get('/branding/');
      const data = response.data;
      setBranding({
        organizationId: data.organization_id,
        organizationName: data.organization_name,
        logoUrl: data.logo_url,
        primaryColor: data.primary_color || DEFAULT_BRANDING.primaryColor,
        secondaryColor: data.secondary_color || DEFAULT_BRANDING.secondaryColor,
        faviconUrl: data.favicon_url,
        emailSignatureName: data.email_signature_name || data.organization_name,
        // Platform constants
        platformName: data.platform_name,
        platformLogoDark: data.platform_logo_dark,
        platformLogoLight: data.platform_logo_light,
        poweredByText: data.powered_by_text,
        emailFooterText: data.email_footer_text,
        pdfFooterText: data.pdf_footer_text,
        isLoaded: true
      });
    } catch (error) {
      // Network error or unexpected auth failure — silently use defaults
      setBranding({ ...DEFAULT_BRANDING, isLoaded: true });
    }
  }, []);

  useEffect(() => {
    if (branding.isLoaded) {
      const root = document.documentElement;

      root.style.setProperty('--org-primary', branding.primaryColor);
      root.style.setProperty('--org-secondary', branding.secondaryColor);
      root.style.setProperty('--org-primary-light', adjustColor(branding.primaryColor, 20));
      root.style.setProperty('--org-primary-dark', adjustColor(branding.primaryColor, -20));
      root.style.setProperty('--org-secondary-light', adjustColor(branding.secondaryColor, 20));
      root.style.setProperty('--org-secondary-dark', adjustColor(branding.secondaryColor, -20));

      const [h, s, l] = hexToHsl(branding.primaryColor);
      root.style.setProperty('--primary', `${h} ${s}% ${l}%`);
      root.style.setProperty('--primary-foreground', l > 55 ? '0 0% 10%' : '0 0% 100%');

      setCssVariablesApplied(true);
    }
  }, [branding]);

  // Update favicon if organization has one
  useEffect(() => {
    if (branding.faviconUrl) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = branding.faviconUrl.startsWith('http') 
        ? branding.faviconUrl 
        : `${API_URL}${branding.faviconUrl}`;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [branding.faviconUrl]);

  // Load branding on mount and whenever the user successfully logs in
  useEffect(() => {
    loadBranding();

    const handleLogin = () => loadBranding();
    window.addEventListener('auth:login', handleLogin);
    return () => window.removeEventListener('auth:login', handleLogin);
  }, [loadBranding]);

  // Expose refresh function
  const refreshBranding = useCallback(() => {
    loadBranding();
  }, [loadBranding]);

  const value = {
    ...branding,
    refreshBranding,
    cssVariablesApplied
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function adjustColor(color, amount) {
  const clamp = (num) => Math.min(255, Math.max(0, num));
  
  // Remove # if present
  let hex = color.replace('#', '');
  
  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Adjust brightness
  const newR = clamp(r + amount);
  const newG = clamp(g + amount);
  const newB = clamp(b + amount);
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

export default BrandingContext;
